<script setup>
/* eslint-disable vue/v-on-event-hyphenation -- mesmo contrato de eventos do ConversationCard */
// Aceleres 2026-09-14: linha de conversa das inboxes Channel::Email, no
// desenho do Gmail (remetente | assunto - previa | data). So o
// ConversationItem decide quem usa este cartao; WhatsApp e demais canais
// continuam no ConversationCard. As regras de clique, selecao e nao-lida sao
// as mesmas de la, para a caixa e os filtros se comportarem igual.
import { computed, inject, onBeforeUnmount, ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { contactAgendaName } from 'dashboard/helper/contactAgendaName';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  shouldPreserveAssignedUnreadForBox,
  viewerOwnsUnreadState,
} from 'dashboard/helper/unreadOwnership';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Avatar from 'next/avatar/Avatar.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import { activateMenu, deactivateMenu } from './contextMenu/activeMenuRegistry';
import { getPointerViewportPosition } from 'dashboard/helper/dashboardZoom';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  activeAssigneeTab: { type: String, default: '' },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectConversation',
  'deSelectConversation',
  'contextMenuToggle',
  'updateConversationStatus',
  'assignAgent',
  'assignSelf',
  'startConversation',
  'assignMine',
  'markAsUnread',
  'markAsRead',
  'togglePin',
]);

// Menu do botao direito enxuto para e-mail: so o fluxo de caixa de entrada.
// Funil, etiqueta, classificacao e transferencia de dono sao do WhatsApp.
const EMAIL_MENU_OPTIONS = [
  'mark-as-read',
  'mark-as-unread',
  'pin',
  'status',
  'agent',
];

const store = useStore();
const router = useRouter();
const { getPlainText } = useMessageFormatter();

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const aclSelectionMode = inject('aclSelectionMode', ref(false));

const inbox = computed(() =>
  props.chat.inbox_id
    ? store.getters['inboxes/getInbox'](props.chat.inbox_id)
    : {}
);
const sender = computed(() => props.chat.meta?.sender || {});
const assignee = computed(() => props.chat.meta?.assignee || {});

const senderName = computed(
  () =>
    contactAgendaName(sender.value, props.chat.inbox_id, '') ||
    sender.value.email ||
    'Remetente desconhecido'
);

const isActiveChat = computed(() => currentChat.value?.id === props.chat.id);

const hasUnread = computed(() => {
  const inboxAttributes = inbox.value?.additional_attributes || {};
  const owns = viewerOwnsUnreadState({
    currentUserId: currentUserId.value,
    assigneeId: assignee.value?.id,
    preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
      props.activeAssigneeTab
    ),
    personalInboxAttributes: {
      ...inboxAttributes,
      connection_type:
        inboxAttributes.connection_type || props.chat?.inbox_connection_type,
      owner_chatwoot_user_id:
        inboxAttributes.owner_chatwoot_user_id ||
        props.chat?.inbox_owner_chatwoot_user_id,
    },
    personalConversationOwnerId:
      props.chat?.additional_attributes?.personal_owner_chatwoot_user_id,
  });
  return owns && (props.chat.unread_count || 0) > 0;
});

const lastMessage = computed(() => getLastMessage(props.chat));

const subject = computed(
  () =>
    props.chat.additional_attributes?.mail_subject ||
    lastMessage.value?.content_attributes?.email?.subject ||
    '(sem assunto)'
);

const preview = computed(() => {
  const msg = lastMessage.value;
  if (!msg) return '';
  const email = msg.content_attributes?.email || {};
  const raw =
    msg.content || email.text_content?.reply || email.text_content?.full || '';
  return getPlainText(raw).replace(/\s+/g, ' ').trim();
});

// Como o Gmail: hora se for de hoje, dia e mes se nao; ano so se for outro.
const receivedAt = computed(() => {
  const seconds = Number(props.chat.timestamp || props.chat.created_at);
  if (!seconds) return '';
  const date = new Date(seconds * 1000);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
});

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  )
);

const isMobileViewport = useMediaQuery('(max-width: 767px)');
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });
const contextMenuOwner = Symbol('email-conversation-card');

const closeContextMenu = () => {
  deactivateMenu(contextMenuOwner);
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

const openContextMenu = e => {
  e.preventDefault();
  e.stopPropagation();
  if (showContextMenu.value) return;
  contextMenu.value = getPointerViewportPosition(e);
  activateMenu({ owner: contextMenuOwner, close: closeContextMenu });
  emit('contextMenuToggle', true);
  showContextMenu.value = true;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};
const onAssignAgent = agent => {
  closeContextMenu();
  emit('assignAgent', agent, [props.chat.id]);
};
const onAssignSelf = () => {
  closeContextMenu();
  emit('assignSelf', props.chat.id);
};
const onStartConversation = () => {
  closeContextMenu();
  emit('startConversation', props.chat.id);
};
const onAssignMine = () => {
  closeContextMenu();
  emit('assignMine', props.chat.id);
};
const onMarkAsUnread = () => {
  closeContextMenu();
  emit('markAsUnread', props.chat.id);
};
const onMarkAsRead = () => {
  closeContextMenu();
  emit('markAsRead', props.chat.id);
};
const onTogglePin = () => {
  closeContextMenu();
  emit('togglePin', props.chat.id);
};

onBeforeUnmount(() => {
  if (showContextMenu.value) closeContextMenu();
});

const toggleSelect = () => {
  if (props.selected) {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('selectConversation', props.chat.id, inbox.value.id);
  }
};

const onClick = e => {
  if (aclSelectionMode.value) {
    toggleSelect();
    return;
  }
  const path = conversationPath.value;
  if (!path) return;
  if (e.metaKey || e.ctrlKey) {
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }
  if (isActiveChat.value) return;

  if (Number(props.chat.unread_count) > 0) {
    store
      .dispatch('markMessagesRead', {
        id: props.chat.id,
        auto: true,
        conversation: props.chat,
      })
      .catch(() => {});
  }
  store.dispatch('previewActiveChat', { data: props.chat });
  if (props.chat?.dataFetched !== true) {
    store.dispatch('getConversation', props.chat.id);
  }
  router.push({ path });
};
</script>

<template>
  <div
    class="acl-email-card flex items-center gap-3 px-3 py-2.5 cursor-pointer border-0 border-b border-solid border-n-weak"
    :class="{
      'bg-white dark:bg-n-solid-2': hasUnread && !isActiveChat && !selected,
      'bg-n-slate-2 dark:bg-n-solid-1':
        !hasUnread && !isActiveChat && !selected,
      'bg-n-background': isActiveChat,
      'bg-n-slate-3': selected,
    }"
    @click="onClick"
    @contextmenu="openContextMenu"
  >
    <button
      v-if="aclSelectionMode"
      type="button"
      class="shrink-0 size-4 rounded border border-solid border-n-slate-8"
      :class="{ 'bg-n-brand': selected }"
      :aria-pressed="selected"
      :aria-label="selected ? 'Desmarcar' : 'Selecionar'"
      @click.stop="toggleSelect"
    />
    <Avatar
      :name="senderName"
      :src="sender.thumbnail"
      :size="32"
      class="shrink-0"
      hide-offline-status
      rounded-full
    />
    <div class="flex-1 min-w-0">
      <div class="flex items-baseline gap-2 min-w-0">
        <span
          class="truncate text-sm"
          :class="
            hasUnread
              ? 'font-semibold text-n-slate-12'
              : 'font-normal text-n-slate-11'
          "
        >
          {{ senderName }}
        </span>
        <span
          class="ml-auto shrink-0 text-xs"
          :class="
            hasUnread ? 'font-semibold text-n-slate-12' : 'text-n-slate-10'
          "
        >
          {{ receivedAt }}
        </span>
      </div>
      <p class="m-0 truncate text-sm" :title="`${subject} - ${preview}`">
        <span
          :class="
            hasUnread
              ? 'font-semibold text-n-slate-12'
              : 'font-normal text-n-slate-11'
          "
        >
          {{ subject }}
        </span>
        <span v-if="preview" class="text-n-slate-10 font-normal">
          - {{ preview }}
        </span>
      </p>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :mobile-sheet="isMobileViewport"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :mobile-sheet="isMobileViewport"
        :status="chat.status"
        :conversation-status="chat.status"
        :inbox-id="inbox.id"
        :team-id="chat.team_id || chat.meta?.team?.id || teamId"
        :priority="chat.priority"
        :is-pinned="!!chat.is_pinned"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels || []"
        :conversation-url="conversationPath"
        :allowed-options="EMAIL_MENU_OPTIONS"
        :active-assignee-tab="activeAssigneeTab"
        :assignee-id="assignee?.id"
        :current-user-id="currentUserId"
        :additional-attributes="chat.additional_attributes"
        :inbox-connection-type="inbox?.additional_attributes?.connection_type"
        :contact="sender"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-self="onAssignSelf"
        @start-conversation="onStartConversation"
        @assign-mine="onAssignMine"
        @mark-as-unread="onMarkAsUnread"
        @mark-as-read="onMarkAsRead"
        @toggle-pin="onTogglePin"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
